import {
  buildQaLabelScopeEntryKey,
  doesQaLabelAffectConcreteRep,
  qaLabelToScopeEntry,
} from '@resistapp/common/qa-label-utils';
import { QaLabel, QaLabelScope, QaLabelScopeEntry, QaLabelType } from '@resistapp/common/types';
import { groupBy } from 'lodash';
import { QaConcreteRepSelection } from './qa-label-selection-utils';

export interface QaLabelRestoreBatch {
  scope: QaLabelScope;
  labelType: QaLabelType;
  appliesToResults: boolean;
  tm: number | null;
  note: string;
  url: string;
  entries: QaLabelScopeEntry[];
}

export interface QaLabelConcreteRepCoverage {
  coverageByType: Record<QaLabelType, number>;
  resultCoverageByType: Record<QaLabelType, number>;
}

export interface QaLabelResultStatusApplyPlan {
  labelIdsToEnable: number[];
  missingEntries: QaLabelScopeEntry[];
}

export interface QaLabelTmShortcutDraft {
  nextTmText: string;
  replaceDraft: boolean;
}

export interface QaLabelTmInputSelectionRange {
  selectionStart: number;
  selectionEnd: number;
}

function buildQaLabelRestoreGroupKey(label: QaLabel): string {
  return JSON.stringify([label.scope, label.labelType, label.appliesToResults, label.tm, label.note, label.url]);
}

function buildQaLabelTmRewriteGroupKey(label: QaLabel): string {
  return JSON.stringify([label.scope, label.labelType, label.appliesToResults, label.note, label.url]);
}

export function buildQaLabelRestoreBatches(labels: QaLabel[]): QaLabelRestoreBatch[] {
  return Object.values(groupBy(labels, label => buildQaLabelRestoreGroupKey(label))).map(group => {
    const firstLabel = group[0];
    return {
      scope: firstLabel.scope,
      labelType: firstLabel.labelType,
      appliesToResults: firstLabel.appliesToResults,
      tm: firstLabel.tm,
      note: firstLabel.note,
      url: firstLabel.url,
      entries: group
        .map(label => qaLabelToScopeEntry(label))
        .sort((a, b) =>
          buildQaLabelScopeEntryKey(firstLabel.scope, a).localeCompare(buildQaLabelScopeEntryKey(firstLabel.scope, b)),
        ),
    };
  });
}

export function buildQaLabelTmRewriteBatches(labels: QaLabel[], nextTm: number): QaLabelRestoreBatch[] {
  return Object.values(groupBy(labels, label => buildQaLabelTmRewriteGroupKey(label))).map(group => {
    const firstLabel = group[0];
    return {
      scope: firstLabel.scope,
      labelType: firstLabel.labelType,
      appliesToResults: firstLabel.appliesToResults,
      tm: nextTm,
      note: firstLabel.note,
      url: firstLabel.url,
      entries: group
        .map(label => qaLabelToScopeEntry(label))
        .sort((a, b) =>
          buildQaLabelScopeEntryKey(firstLabel.scope, a).localeCompare(buildQaLabelScopeEntryKey(firstLabel.scope, b)),
        ),
    };
  });
}

export function getQaLabelShortcutDigit(code: string, key: string): string | null {
  const codeMatch = code.match(/^(Digit|Numpad)([0-9])$/);
  return codeMatch?.[2] ?? (/^[0-9]$/.test(key) ? key : null);
}

export function getQaLabelTmShortcutDigit(_code: string, key: string): string | null {
  return /^[0-9]$/.test(key) ? key : null;
}

export function appendQaLabelTmShortcutDigit(
  currentTmText: string,
  digit: string,
  replaceDraft: boolean,
): QaLabelTmShortcutDraft {
  return {
    nextTmText: `${replaceDraft ? '' : currentTmText}${digit}`.slice(0, 2),
    replaceDraft: false,
  };
}

export function getQaLabelTmInputSelectionRange(
  currentTmText: string,
  replaceDraft: boolean,
): QaLabelTmInputSelectionRange {
  return replaceDraft
    ? {
        selectionStart: 0,
        selectionEnd: currentTmText.length,
      }
    : {
        selectionStart: currentTmText.length,
        selectionEnd: currentTmText.length,
      };
}

export function buildQaLabelResultStatusApplyPlan(
  labels: QaLabel[],
  scope: QaLabelScope,
  labelType: QaLabelType,
  entries: QaLabelScopeEntry[],
): QaLabelResultStatusApplyPlan {
  const selectedEntryKeys = new Set(entries.map(entry => buildQaLabelScopeEntryKey(scope, entry)));
  const matchingLabels = labels.filter(label => {
    if (label.scope !== scope || label.labelType !== labelType) {
      return false;
    }

    return selectedEntryKeys.has(buildQaLabelScopeEntryKey(scope, qaLabelToScopeEntry(label)));
  });
  const coveredEntryKeys = new Set(matchingLabels.map(label => buildQaLabelScopeEntryKey(scope, qaLabelToScopeEntry(label))));

  return {
    labelIdsToEnable: matchingLabels.filter(label => !label.appliesToResults).map(label => label.id),
    missingEntries: entries.filter(entry => !coveredEntryKeys.has(buildQaLabelScopeEntryKey(scope, entry))),
  };
}

export function buildQaLabelConcreteRepCoverage(
  labels: QaLabel[],
  selectedConcreteReps: QaConcreteRepSelection[],
): QaLabelConcreteRepCoverage {
  const coveredRepKeysByType = createQaLabelRepKeyMap();
  const resultCoveredRepKeysByType = createQaLabelRepKeyMap();

  labels.forEach(label => {
    selectedConcreteReps.forEach(rep => {
      if (!doesQaLabelAffectConcreteRep(label, rep)) {
        return;
      }

      const repKey = `${rep.sampleId}|${rep.assay}|${rep.techRep}`;
      coveredRepKeysByType[label.labelType].add(repKey);
      if (label.appliesToResults) {
        resultCoveredRepKeysByType[label.labelType].add(repKey);
      }
    });
  });

  return {
    coverageByType: qaLabelTypeCountsFromRepKeys(coveredRepKeysByType),
    resultCoverageByType: qaLabelTypeCountsFromRepKeys(resultCoveredRepKeysByType),
  };
}

function qaLabelTypeCountsFromRepKeys(repKeysByType: Record<QaLabelType, Set<string>>): Record<QaLabelType, number> {
  return {
    [QaLabelType.NO_PRODUCT]: repKeysByType[QaLabelType.NO_PRODUCT].size,
    [QaLabelType.PRODUCT]: repKeysByType[QaLabelType.PRODUCT].size,
    [QaLabelType.PRIMER_DIMER]: repKeysByType[QaLabelType.PRIMER_DIMER].size,
    [QaLabelType.UNSPECIFIC]: repKeysByType[QaLabelType.UNSPECIFIC].size,
    [QaLabelType.NONSENSE]: repKeysByType[QaLabelType.NONSENSE].size,
    [QaLabelType.ISSUE]: repKeysByType[QaLabelType.ISSUE].size,
    [QaLabelType.OTHER]: repKeysByType[QaLabelType.OTHER].size,
  };
}

function createQaLabelRepKeyMap(): Record<QaLabelType, Set<string>> {
  return {
    [QaLabelType.NO_PRODUCT]: new Set<string>(),
    [QaLabelType.PRODUCT]: new Set<string>(),
    [QaLabelType.PRIMER_DIMER]: new Set<string>(),
    [QaLabelType.UNSPECIFIC]: new Set<string>(),
    [QaLabelType.NONSENSE]: new Set<string>(),
    [QaLabelType.ISSUE]: new Set<string>(),
    [QaLabelType.OTHER]: new Set<string>(),
  };
}
